/**
 * Back and next through the editor, in surface order.
 *
 * The editor used to be a wizard, and the pair of buttons at the foot of each
 * step is the part of it worth keeping: a reader who has finished a section
 * wants the next one without going back up to the strip to find it.
 *
 * @module router/programme-editor/SectionNav
 */

import React from "react";

import { useLocation, useNavigate, useParams } from "react-router";

import { BUILDER_SURFACES, programmeRoleFor } from "@/components/shared/programme-role";
import { Button } from "@/components/ui";
import { useProgramme } from "@/hooks/useStore";
import { useOptionalWorkspaceContext } from "@/router/workspace-context";

import { useEditorBase } from "./SurfaceLayout";
import { applicableStateKeys, isStateKeyUnlocked, SURFACES } from "./surfaces";

interface Stop {
  path: string;
  /** The section's own name, which is a tab and not in the sidebar. */
  title: string;
  /** The surface's name, which is. */
  surface: string;
}

/**
 * What a button names: the sidebar entry when it leaves the surface the reader
 * is on, the tab when it stays within it. A tab is not somewhere the sidebar
 * can take you, so naming one across surfaces points at nothing in it.
 */
function labelFor(stop: Stop, from: Stop): string {
  return stop.surface === from.surface ? stop.title : stop.surface;
}

/** Every section, in the order the surfaces list them, that is open today. */
function stops(base: string, builder: boolean): Stop[] {
  if (builder) {
    // One stop per surface a builder reaches: their Overview is a single
    // summary, whatever section the address names.
    return SURFACES.filter((surface) => BUILDER_SURFACES.includes(surface.key)).map((surface) => ({
      path: `${base}/${surface.segment}`,
      title: surface.title,
      surface: surface.title,
    }));
  }
  return SURFACES.flatMap((surface) =>
    surface.sections
      .filter(
        (section) =>
          section.stateKeys.length === 0 ||
          applicableStateKeys(section.stateKeys).some((key) => isStateKeyUnlocked(key)),
      )
      .map((section) => ({
        path: section.segment
          ? `${base}/${surface.segment}/${section.segment}`
          : `${base}/${surface.segment}`,
        title: section.title,
        surface: surface.title,
      })),
  );
}

export function SectionNav(): React.ReactElement | null {
  // Subscribed, not read: which stops exist comes from the legacy store.
  useProgramme();
  const base = useEditorBase();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { programmeId } = useParams<{ programmeId: string }>();
  const session = useOptionalWorkspaceContext()?.session ?? null;
  const list = stops(base, programmeRoleFor(session, programmeId) === "builder");
  // The longest stop the address sits in, not the first: a surface's own
  // address is a prefix of every section under it, so the first match put a
  // reader on Coverage one stop behind, offering "Next: Coverage".
  const index = list.reduce(
    (best, stop, at) =>
      (pathname === stop.path || pathname.startsWith(`${stop.path}/`)) &&
      (best === -1 || stop.path.length > list[best]!.path.length)
        ? at
        : best,
    -1,
  );

  if (index === -1) {
    return null;
  }

  const previous = list[index - 1];
  const next = list[index + 1];

  return (
    <nav
      // Stacked on a phone so each label reads in full, a row from `sm` up; a
      // button with nowhere to go is left out rather than drawn dead.
      className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-between"
      aria-label="Editor sections"
    >
      {previous ? (
        <Button
          variant="outline"
          icon="arrow-left"
          className="h-auto min-h-10 min-w-0 py-2 whitespace-normal max-sm:w-full"
          onClick={() => navigate(previous.path)}
          data-testid="back-btn"
        >
          <span>Back: {labelFor(previous, list[index]!)}</span>
        </Button>
      ) : null}
      {next ? (
        <Button
          variant="primary"
          className="ml-auto h-auto min-h-10 min-w-0 py-2 whitespace-normal max-sm:w-full"
          onClick={() => navigate(next.path)}
          data-testid="next-btn"
        >
          <span>Next: {labelFor(next, list[index]!)}</span>
        </Button>
      ) : null}
    </nav>
  );
}
